import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'

import { Camera } from '../components/Camera'
import { Button, Empty } from '../components/Card'
import { Segmented } from '../components/fields'
import { choiceRow, choiceTone } from '../components/fields/Field'
import { Icon } from '../components/Icon'
import { PhotoCompare } from '../components/PhotoCompare'
import { PhotoImage } from '../components/PhotoImage'
import { PhotoViewer } from '../components/PhotoViewer'
import { Screen, SectionLabel } from '../components/Screen'
import { SheetShell } from '../components/Sheet'
import { useToast } from '../components/Toast'
import { t, term } from '../i18n'
import { api } from '../lib/api'
import { longDate } from '../lib/format'
import { prepare } from '../lib/image'
import { factKeys, photosQuery } from '../lib/queries'
import { haptic } from '../lib/telegram'
import type { Photo, PhotoAngle } from '../lib/types'

const route = getRouteApi('/photos')

export const ANGLES: PhotoAngle[] = ['front', 'left', 'right', 'detail']

export function Photos() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const picker = useRef<HTMLInputElement>(null)
  const [source, setSource] = useState(false)
  const [camera, setCamera] = useState(false)
  const [available, setAvailable] = useState(false)
  const [viewing, setViewing] = useState<Photo>()
  const [comparing, setComparing] = useState<Photo>()

  useEffect(() => {
    const media = navigator.mediaDevices
    if (!media?.getUserMedia) return
    void media
      .enumerateDevices()
      .then((devices) => setAvailable(devices.some((device) => device.kind === 'videoinput')))
      .catch(() => setAvailable(true))
  }, [])

  const angle = search.angle ?? 'front'
  const { data: photos } = useSuspenseQuery(photosQuery(angle))

  const upload = useMutation({
    mutationFn: async (file: Blob) =>
      api.uploadPhoto(angle, file instanceof File ? await prepare(file) : file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: factKeys.photos })
      haptic.done()
      setCamera(false)
    },
    onError: (error: Error) => {
      haptic.fail()
      toast(error.message, 'error')
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.deletePhoto(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: factKeys.photos })
      haptic.done()
      setViewing(undefined)
    },
    onError: (error: Error) => {
      haptic.fail()
      toast(error.message, 'error')
    },
  })

  const [latest, previous] = photos

  return (
    <Screen nested back="/more" title={t.photos.title}>
      <Segmented
        value={angle}
        onChange={(value) => {
          haptic.select()
          void navigate({ search: { angle: value as PhotoAngle }, replace: true })
        }}
        options={ANGLES.map((option) => ({ value: option, label: term('angle', option) }))}
        ariaLabel={t.photos.angleLabel}
      />

      <input
        ref={picker}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ''
          if (file) upload.mutate(file)
        }}
      />

      <div className="mt-4">
        <Button disabled={upload.isPending} onClick={() => setSource(true)}>
          {upload.isPending ? t.photos.uploading : t.photos.add}
        </Button>
      </div>

      {latest ? (
        <>
          <SectionLabel>{t.photos.latest}</SectionLabel>
          <div className="grid grid-cols-2 gap-2">
            <figure>
              <button
                type="button"
                onClick={() => setViewing(latest)}
                className="pressable block w-full"
              >
                <PhotoImage
                  id={latest.id}
                  alt={term('angle', latest.angle)}
                  className="aspect-3/4"
                />
              </button>
              <figcaption className="mt-1 text-hint text-text-muted">
                {longDate(latest.takenAt.slice(0, 10))}
              </figcaption>
            </figure>
            {previous ? (
              <figure>
                <button
                  type="button"
                  onClick={() => setViewing(previous)}
                  className="pressable block w-full"
                >
                  <PhotoImage
                    id={previous.id}
                    alt={term('angle', previous.angle)}
                    className="aspect-3/4"
                  />
                </button>
                <figcaption className="mt-1 text-hint text-text-muted">
                  {t.photos.previous} {longDate(previous.takenAt.slice(0, 10))}
                </figcaption>
              </figure>
            ) : null}
          </div>
        </>
      ) : (
        <div className="mt-4">
          <Empty title={t.photos.emptyTitle} hint={t.photos.emptyHint} />
        </div>
      )}

      {photos.length > 2 ? (
        <>
          <SectionLabel>{t.photos.earlier}</SectionLabel>
          <div className="grid grid-cols-3 gap-2">
            {photos.slice(2).map((photo) => (
              <button
                key={photo.id}
                type="button"
                onClick={() => setViewing(photo)}
                className="pressable block w-full"
              >
                <PhotoImage id={photo.id} alt={term('angle', photo.angle)} className="aspect-3/4" />
              </button>
            ))}
          </div>
        </>
      ) : null}

      {source ? (
        <SheetShell
          title={t.photos.sourceTitle}
          subtitle={previous ? t.photos.sourceHint : undefined}
          onClose={() => setSource(false)}
        >
          <div className="flex flex-col gap-2 pb-2 pt-2">
            <button
              type="button"
              disabled={!available}
              onClick={() => {
                setSource(false)
                setCamera(true)
              }}
              className={`${choiceRow} ${choiceTone(false)} disabled:opacity-40`}
            >
              <Icon name="camera" size={20} />
              <span className="flex-1">
                {t.photos.camera}
                {available ? null : (
                  <span className="mt-[3px] block text-meta text-text-muted">
                    {t.photos.cameraHint}
                  </span>
                )}
              </span>
            </button>
            <button
              type="button"
              onClick={() => {
                setSource(false)
                picker.current?.click()
              }}
              className={`${choiceRow} ${choiceTone(false)}`}
            >
              <Icon name="photo" size={20} />
              <span className="flex-1">{t.photos.gallery}</span>
            </button>
          </div>
        </SheetShell>
      ) : null}

      {viewing ? (
        <PhotoViewer
          photo={viewing}
          comparable={photos.length > 1}
          deleting={remove.isPending}
          onCompare={() => {
            setComparing(viewing)
            setViewing(undefined)
          }}
          onDelete={() => remove.mutate(viewing.id)}
          onClose={() => setViewing(undefined)}
        />
      ) : null}

      {comparing ? (
        <PhotoCompare photos={photos} initial={comparing} onClose={() => setComparing(undefined)} />
      ) : null}

      {camera ? (
        <Camera
          angle={angle}
          angles={ANGLES}
          previous={latest}
          saving={upload.isPending}
          onAngle={(next) => void navigate({ search: { angle: next }, replace: true })}
          onSave={(blob) => upload.mutate(blob)}
          onFallback={() => {
            setCamera(false)
            picker.current?.click()
          }}
          onClose={() => setCamera(false)}
        />
      ) : null}
    </Screen>
  )
}
